// features/bookmarks/ManageFoldersDialog.tsx — create/rename/delete folders.
import { useState } from 'react';
import styled from 'styled-components';
import { Dialog, TextField, Button, IconButton } from '@/ui';
import type { BookmarkFolder } from '@/core/models/bookmark';

export interface ManageFoldersDialogProps {
  open: boolean;
  onClose: () => void;
  folders: BookmarkFolder[];
  onCreate: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}

const AddRow = styled.div`display: flex; gap: 8px; align-items: flex-end; margin-bottom: 16px;`;
const Grow = styled.div`flex: 1;`;
const Row = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 6px 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const NameField = styled.input`
  flex: 1; padding: 7px 10px; border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
`;

export function ManageFoldersDialog({ open, onClose, folders, onCreate, onRename, onDelete }: ManageFoldersDialogProps) {
  const [name, setName] = useState('');

  function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setName('');
  }
  function rename(id: string, next: string, prev: string) {
    const trimmed = next.trim();
    if (trimmed && trimmed !== prev) onRename(id, trimmed);
  }

  return (
    <Dialog open={open} onClose={onClose} title="Manage Folders">
      <AddRow>
        <Grow>
          <TextField label="New folder name" value={name} onChange={(e) => setName(e.target.value)} />
        </Grow>
        <Button onClick={add}>Add</Button>
      </AddRow>
      {folders.map((f) => (
        <Row key={f.id}>
          <NameField
            aria-label={`Folder name ${f.name}`}
            defaultValue={f.name}
            onBlur={(e) => rename(f.id, e.target.value, f.name)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          />
          {f.id !== 'unsorted' && (
            <IconButton icon="trash" label={`Delete ${f.name}`} size={16} onClick={() => onDelete(f.id)} />
          )}
        </Row>
      ))}
    </Dialog>
  );
}
